import { relativeTime } from '@bsync/shared';
import { A, useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import { Show, createSignal, onCleanup, onMount, type ParentProps } from 'solid-js';
import { retrySync, signOut, state } from '../lib/app';
import { FolderTree } from './FolderTree';
import { IconBookmarks, IconClose, IconDevices, IconMenu, IconPuzzle, IconRefresh, Logo } from './Icons';
import { Toasts } from './Toasts';

export type Tone = 'live' | 'syncing' | 'offline' | 'error' | 'idle';

export function syncTone(): Tone {
  const s = state.status();
  if (!state.online() || s?.phase === 'offline') return 'offline';
  if (!s) return 'idle';
  if (s.phase === 'error') return 'error';
  if (s.phase === 'syncing') return 'syncing';
  return state.realtime() === 'live' ? 'live' : 'idle';
}

export function Shell(props: ParentProps) {
  const [drawer, setDrawer] = createSignal(false);
  const [now, setNow] = createSignal(Date.now());
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const currentFolder = () => (typeof params.folder === 'string' ? params.folder : 'root:toolbar');

  onMount(() => {
    const t = setInterval(() => setNow(Date.now()), 20_000);
    onCleanup(() => clearInterval(t));
  });

  const openFolder = (id: string) => {
    setDrawer(false);
    navigate(`/?folder=${encodeURIComponent(id)}`);
  };

  return (
    <div class="app">
      <div class={`ribbon ${syncTone()}`} role="presentation" />
      <header class="topbar">
        <button class="icon-btn" onClick={() => setDrawer(true)} aria-label="Open menu">
          <IconMenu />
        </button>
        <A href="/" class="brand">
          <Logo />
          bsync
        </A>
        <SyncDot />
      </header>
      <div class="layout">
        <div class="scrim" classList={{ show: drawer() }} onClick={() => setDrawer(false)} />
        <aside class="rail" classList={{ open: drawer() }}>
          <div class="rail-head">
            <A href="/" class="brand" onClick={() => setDrawer(false)}>
              <Logo />
              bsync
            </A>
            <button class="icon-btn drawer-close" onClick={() => setDrawer(false)} aria-label="Close menu">
              <IconClose />
            </button>
          </div>
          <nav class="nav" aria-label="Sections">
            <A href="/" end class="nav-item" classList={{ active: location.pathname === '/' }} onClick={() => setDrawer(false)}>
              <IconBookmarks /> Bookmarks
            </A>
            <A href="/extensions" class="nav-item" onClick={() => setDrawer(false)}>
              <IconPuzzle /> Extensions
            </A>
            <A href="/devices" class="nav-item" onClick={() => setDrawer(false)}>
              <IconDevices /> Devices
            </A>
          </nav>
          <Show when={state.tree()}>
            <div class="rail-section">
              <FolderTree current={location.pathname === '/' ? currentFolder() : ''} onOpen={openFolder} />
            </div>
          </Show>
          <div class="rail-foot">
            <SyncPanel now={now()} />
            <div class="account">
              <span class="ellipsis" title={state.session()?.user.email ?? ''}>
                {state.session()?.user.email}
              </span>
              <button class="link" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          </div>
        </aside>
        <main class="main">{props.children}</main>
      </div>
      <Toasts />
    </div>
  );
}

function SyncDot() {
  return <span class={`dot ${syncTone()}`} aria-label={`Sync: ${syncTone()}`} />;
}

function SyncPanel(props: { now: number }) {
  const s = () => state.status();
  const tone = syncTone;
  const [busy, setBusy] = createSignal(false);
  const headline = () => {
    switch (tone()) {
      case 'offline':
        return 'Offline';
      case 'error':
        return 'Sync paused';
      case 'syncing':
        return 'Syncing';
      default:
        return (s()?.pending ?? 0) > 0 ? 'Saving changes' : 'Up to date';
    }
  };
  const detail = () => {
    const st = s();
    if (!st) return 'Connecting';
    if (tone() === 'offline') return st.pending ? `${st.pending} change${st.pending === 1 ? '' : 's'} saved here, will upload when you’re back online` : 'Changes you make are saved here and upload later';
    if (tone() === 'error') return st.lastError ?? 'Something went wrong';
    const when = st.lastSyncAt ? `Synced ${relativeTime(st.lastSyncAt, props.now)}` : 'Not synced yet';
    return state.realtime() === 'live' ? `${when}, live updates on` : `${when}, checking every minute`;
  };
  const retry = async () => {
    setBusy(true);
    await retrySync();
    setBusy(false);
  };
  return (
    <div class={`sync-panel ${tone()}`}>
      <div class="sync-text">
        <strong>
          <span class={`dot ${tone()}`} /> {headline()}
        </strong>
        <span>{detail()}</span>
      </div>
      <button class="icon-btn" onClick={retry} disabled={busy() || tone() === 'syncing'} aria-label="Sync now" title="Sync now">
        <IconRefresh spinning={busy() || tone() === 'syncing'} />
      </button>
    </div>
  );
}
